import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import AddLinkModal from './modals/AddLinkModal';
import AddFolderModal from './modals/AddFolderModal';
import AddChildLinkModal from './modals/AddChildLinkModal';
import IconSelectorModal from './modals/IconSelectorModal';

const JsonBuilder = () => {
  const navigate = useNavigate();
  // State pentru datele principale
  const [profileData, setProfileData] = useState({
    name: 'Nume Utilizator',
    description: 'Descriere profil',
    avatar: '/avatar.jpg',
    copyright: 'LinkTree' // Text pentru copyright
  });

  const [themeData, setThemeData] = useState({
    backgroundColor: 'bg-gradient-to-br from-purple-600 to-blue-500',
    cardBackground: 'bg-white',
    textColor: 'text-gray-800',
    linkHoverColor: 'bg-gray-100'
  });

  const [links, setLinks] = useState([]);
  const [activeTab, setActiveTab] = useState('links'); // 'profile', 'theme', 'links'
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingChildId, setEditingChildId] = useState(null);
  const fileInputRef = useRef(null);

  // State pentru selector de iconițe
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [currentIconTarget, setCurrentIconTarget] = useState(null); // 'newLink', 'newChildLink', 'editLink-id', 'editChildLink-folderId-childId'

  // State pentru modale
  const [showAddLinkModal, setShowAddLinkModal] = useState(false);
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [showAddChildLinkModal, setShowAddChildLinkModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);

  // State pentru noul link
  const [newLink, setNewLink] = useState({
    platform: 'globe',
    title: '',
    url: '',
    icon: ''
  });

  // State pentru noul folder
  const [newFolder, setNewFolder] = useState({
    type: 'folder',
    platform: 'folder',
    title: '',
    children: []
  });

  // State pentru noul link în folder
  const [newChildLink, setNewChildLink] = useState({
    platform: 'globe',
    title: '',
    url: '',
    icon: ''
  });

  // State pentru verificarea dacă rulează în Electron
  const [isElectron, setIsElectron] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Încarcă datele inițiale
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Verifică dacă rulează în Electron
        if (window.electronAPI) {
          const electronCheck = await window.electronAPI.isElectron();
          setIsElectron(electronCheck);
          
          // Încarcă din Electron
          const result = await window.electronAPI.loadLinks();
          if (result.success && result.data) {
            if (result.data.profile) setProfileData(result.data.profile);
            if (result.data.theme) setThemeData(result.data.theme);
            if (result.data.links) setLinks(result.data.links);
            return;
          }
        }
        
        // Fallback - încearcă să încarce din /public/links.json
        const fallbackResponse = await fetch('/links.json');
        if (fallbackResponse.ok) {
          const data = await fallbackResponse.json();
          if (data.profile) setProfileData(data.profile);
          if (data.theme) setThemeData(data.theme);
          if (data.links) setLinks(data.links);
        }
      } catch (error) {
        console.log('Nu s-au putut încărca datele inițiale (normal la primul start)');
      }
    };

    loadInitialData();
  }, []);

  // Funcție pentru afișarea notificărilor
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  const generateJson = () => {
    return JSON.stringify({
      theme: themeData,
      profile: profileData,
      links: links
    }, null, 2);
  };

  // Salvează JSON-ul direct în /public/links.json
  const saveJson = async () => {
    const data = {
      theme: themeData,
      profile: profileData,
      links: links
    };

    try {
      if (isElectron && window.electronAPI) {
        // Salvare în Electron
        const result = await window.electronAPI.saveLinks(data);
        if (result.success) {
          showNotification('✅ ' + result.message, 'success');
        } else {
          showNotification('❌ Eroare: ' + result.message, 'error');
        }
      } else {
        // Fallback la download pentru browser
        const jsonData = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = 'links.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        showNotification('⚠️ Rulează în browser. Fișierul a fost descărcat. Pentru salvare automată, folosește aplicația Electron.', 'warning');
      }
    } catch (error) {
      console.error('Eroare la salvare:', error);
      showNotification('❌ Eroare la salvare: ' + error.message, 'error');
    }
  };

  // Funcție pentru salvare și preview
  const saveAndPreview = async () => {
    await saveJson();
    navigate('/');
  };

  // Încarcă fișier JSON
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.profile) setProfileData(data.profile);
        if (data.theme) setThemeData(data.theme);
        if (data.links) setLinks(data.links);
      } catch (error) {
        alert('Eroare la încărcarea fișierului JSON: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  // Handler pentru schimbarea profilului
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  // Handler pentru schimbarea temei
  const handleThemeChange = (e) => {
    const { name, value } = e.target;
    setThemeData(prev => ({ ...prev, [name]: value }));
  };

  // Handler pentru schimbarea unui link
  const handleLinkChange = (e, id) => {
    const { name, value } = e.target;
    setLinks(prev => prev.map(link => 
      link.id === id ? { ...link, [name]: value } : link
    ));
  };

  // Handler pentru schimbarea unui link în folder
  const handleChildLinkChange = (e, folderId, childId) => {
    const { name, value } = e.target;
    setLinks(prev => prev.map(link => {
      if (link.id === folderId && link.children) {
        return {
          ...link,
          children: link.children.map(child => 
            child.id === childId ? { ...child, [name]: value } : child
          )
        };
      }
      return link;
    }));
  };

  // Adaugă un nou link
  const addLink = () => {
    if (!newLink.title || !newLink.url) {
      alert('Completează titlul și URL-ul pentru noul link.');
      return;
    }

    const newLinkObj = {
      id: Date.now().toString(),
      ...newLink
    };

    setLinks(prev => [...prev, newLinkObj]);
    setNewLink({
      platform: 'globe',
      title: '',
      url: '',
      icon: ''
    });
    setShowAddLinkModal(false);
  };

  // Adaugă un nou folder
  const addFolder = () => {
    if (!newFolder.title) {
      alert('Completează titlul pentru noul folder.');
      return;
    }

    const newFolderObj = {
      id: Date.now().toString(),
      ...newFolder,
      children: []
    };

    setLinks(prev => [...prev, newFolderObj]);
    setNewFolder({
      type: 'folder',
      platform: 'folder',
      title: '',
      children: []
    });
    setShowAddFolderModal(false);
  };

  // Adaugă un nou link în folder
  const addChildLink = (folderId) => {
    if (!newChildLink.title || !newChildLink.url) {
      alert('Completează titlul și URL-ul pentru noul link.');
      return;
    }

    const newChildLinkObj = {
      id: `${folderId}-${Date.now()}`,
      ...newChildLink
    };

    setLinks(prev => prev.map(link => {
      if (link.id === folderId) {
        return {
          ...link,
          children: [...(link.children || []), newChildLinkObj]
        };
      }
      return link;
    }));

    setNewChildLink({
      platform: 'globe',
      title: '',
      url: '',
      icon: ''
    });
    setShowAddChildLinkModal(false);
  };

  // Șterge un link
  const deleteLink = (id) => {
    setLinks(prev => prev.filter(link => link.id !== id));
  };

  // Șterge un link din folder
  const deleteChildLink = (folderId, childId) => {
    setLinks(prev => prev.map(link => {
      if (link.id === folderId && link.children) {
        return {
          ...link,
          children: link.children.filter(child => child.id !== childId)
        };
      }
      return link;
    }));
  };

  // Funcție pentru a deschide selectorul de iconițe
  const openIconSelector = (target) => {
    setCurrentIconTarget(target);
    setShowIconSelector(true);
  };

  // Funcție pentru a selecta o iconița
  const selectIcon = (iconClass) => {
    if (currentIconTarget === 'newLink') {
      setNewLink({...newLink, icon: iconClass});
    } else if (currentIconTarget === 'newChildLink') {
      setNewChildLink({...newChildLink, icon: iconClass});
    } else if (currentIconTarget === 'newFolder') {
      setNewFolder({...newFolder, icon: iconClass});
    } else if (currentIconTarget?.startsWith('editLink-')) {
      const linkId = currentIconTarget.split('-')[1];
      setLinks(prev => prev.map(link => 
        link.id.toString() === linkId ? { ...link, icon: iconClass } : link
      ));
    } else if (currentIconTarget?.startsWith('editChildLink-')) {
      const parts = currentIconTarget.split('-');
      const folderId = parts[1];
      const childId = parts[2];
      setLinks(prev => prev.map(link => {
        if (link.id.toString() === folderId && link.children) {
          return {
            ...link,
            children: link.children.map(child => 
              child.id.toString() === childId ? { ...child, icon: iconClass } : child
            )
          };
        }
        return link;
      }));
    }
    
    setShowIconSelector(false);
  };

  // Funcția pentru gestionarea evenimentului de drag-and-drop
  const handleDragEnd = (result) => {
    const { source, destination, type } = result;
    
    // Dacă nu există destinație (e.g., drop în afara unui droppable), nu facem nimic
    if (!destination) {
      return;
    }
    
    // Dacă sursa și destinația sunt identice, nu facem nimic
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    
    // Dacă tipul este "LINK" (link-uri din meniul principal)
    if (type === 'LINK') {
      // Verifică dacă încercăm să mutăm un folder într-un alt folder
      if (source.droppableId === 'main-links' && destination.droppableId.startsWith('folder-')) {
        const itemToMove = links[source.index];
        
        // Dacă elementul este un folder, blocăm mutarea și ieșim
        if (itemToMove.type === 'folder') {
          // Nu permitem mutarea folderelor în alte foldere
          return;
        }
      }
      
      // Dacă sursa și destinația sunt în același container (meniul principal)
      if (source.droppableId === 'main-links' && destination.droppableId === 'main-links') {
        // Reordonăm link-urile în meniul principal
        const newLinks = Array.from(links);
        const [removed] = newLinks.splice(source.index, 1);
        newLinks.splice(destination.index, 0, removed);
        setLinks(newLinks);
      } 
      // Dacă link-ul este mutat din meniul principal într-un folder
      else if (source.droppableId === 'main-links' && destination.droppableId.startsWith('folder-')) {
        const folderId = destination.droppableId.replace('folder-', '');
        const folder = links.find(link => link.id.toString() === folderId);
        
        if (folder && folder.type === 'folder') {
          // Extragem link-ul care va fi mutat
          const linkToMove = links[source.index];
          
          // Creăm un nou link pentru a-l adăuga în folder
          const newChildLink = {
            ...linkToMove,
            id: `${folderId}-${Date.now()}`
          };
          
          // Ștergem link-ul din meniul principal și actualizăm folder-ul
          const newLinks = [...links];
          newLinks.splice(source.index, 1); // Ștergem link-ul original
          
          // Adăugăm link-ul în folder
          const updatedLinks = newLinks.map(link => {
            if (link.id.toString() === folderId) {
              const newChildren = [...(link.children || [])];
              newChildren.splice(destination.index, 0, newChildLink); // Adăugăm la poziția corectă
              return {
                ...link,
                children: newChildren
              };
            }
            return link;
          });
          
          setLinks(updatedLinks);
        }
      }
      // Dacă link-ul este mutat dintr-un folder în meniul principal
      else if (source.droppableId.startsWith('folder-') && destination.droppableId === 'main-links') {
        const sourceFolderId = source.droppableId.replace('folder-', '');
        const sourceFolder = links.find(link => link.id.toString() === sourceFolderId);
        
        if (sourceFolder && sourceFolder.children && sourceFolder.children.length > source.index) {
          // Extragem link-ul care va fi mutat
          const linkToMove = { ...sourceFolder.children[source.index] };
          
          // Generăm un ID unic pentru noul link din meniul principal
          const newLink = {
            ...linkToMove,
            id: Date.now().toString()
          };
          
          // Creăm o copie nouă a listei de link-uri
          const newLinks = [...links];
          
          // Adăugăm link-ul în meniul principal la poziția dorită
          newLinks.splice(destination.index, 0, newLink);
          
          // Actualizăm folder-ul sursă prin eliminarea link-ului
          const updatedLinks = newLinks.map(link => {
            if (link.id.toString() === sourceFolderId) {
              return {
                ...link,
                children: link.children.filter((_, index) => index !== source.index)
              };
            }
            return link;
          });
          
          setLinks(updatedLinks);
        }
      }
      // Dacă link-ul este mutat dintr-un folder în alt folder
      else if (source.droppableId.startsWith('folder-') && destination.droppableId.startsWith('folder-')) {
        const sourceFolderId = source.droppableId.replace('folder-', '');
        const destFolderId = destination.droppableId.replace('folder-', '');
        
        // Dacă încercăm să mutăm în același folder, tratăm ca pe o reordonare
        if (sourceFolderId === destFolderId) {
          const updatedLinks = links.map(link => {
            if (link.id.toString() === sourceFolderId && link.children) {
              const newChildren = [...link.children];
              const [removed] = newChildren.splice(source.index, 1);
              newChildren.splice(destination.index, 0, removed);
              return { ...link, children: newChildren };
            }
            return link;
          });
          
          setLinks(updatedLinks);
          return;
        }
        
        const sourceFolder = links.find(link => link.id.toString() === sourceFolderId);
        
        if (sourceFolder && sourceFolder.children && sourceFolder.children.length > source.index) {
          // Extragem link-ul care va fi mutat
          const linkToMove = { ...sourceFolder.children[source.index] };
          
          // Generăm un ID unic pentru noul link în folderul destinație
          const newChildLink = {
            ...linkToMove,
            id: `${destFolderId}-${Date.now()}`
          };
          
          // Actualizăm folderele
          const updatedLinks = links.map(link => {
            // Ștergem din folderul sursă
            if (link.id.toString() === sourceFolderId) {
              return {
                ...link,
                children: link.children.filter((_, index) => index !== source.index)
              };
            }
            // Adăugăm în folderul destinație
            if (link.id.toString() === destFolderId) {
              const newChildren = [...(link.children || [])];
              newChildren.splice(destination.index, 0, newChildLink); // Adăugăm la poziția corectă
              return {
                ...link,
                children: newChildren
              };
            }
            return link;
          });
          
          setLinks(updatedLinks);
        }
      }
    }
  };

  // Renderează selectorul de iconițe
  const renderIconSelector = () => (
    <IconSelectorModal 
      onSelectIcon={selectIcon}
      onClose={() => setShowIconSelector(false)}
    />
  );

  // Renderează tab-ul de profil
  const renderProfileTab = () => (
    <div className="bg-white rounded-lg p-4 shadow-md">
      <h3 className="text-lg font-medium mb-4">Editare Profil</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Nume</label>
        <input
          type="text"
          name="name"
          value={profileData.name}
          onChange={handleProfileChange}
          className="w-full p-2 border rounded"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Descriere</label>
        <textarea
          name="description"
          value={profileData.description}
          onChange={handleProfileChange}
          className="w-full p-2 border rounded"
          rows="3"
        ></textarea>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">URL Avatar (în folderul public)</label>
        <input
          type="text"
          name="avatar"
          value={profileData.avatar}
          onChange={handleProfileChange}
          className="w-full p-2 border rounded"
          placeholder="/avatar.jpg"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Text Copyright</label>
        <input
          type="text"
          name="copyright"
          value={profileData.copyright}
          onChange={handleProfileChange}
          className="w-full p-2 border rounded"
          placeholder="LinkTree"
        />
        <p className="text-sm text-gray-500 mt-1">
          Preview: "© {new Date().getFullYear()} {profileData.copyright || 'LinkTree'}"
        </p>
      </div>
    </div>
  );

  // Renderează tab-ul de temă
  const renderThemeTab = () => (
    <div className="bg-white rounded-lg p-4 shadow-md">
      <h3 className="text-lg font-medium mb-4">Editare Temă</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Culoare Fundal (clasa Tailwind)</label>
        <input
          type="text"
          name="backgroundColor"
          value={themeData.backgroundColor}
          onChange={handleThemeChange}
          className="w-full p-2 border rounded"
          placeholder="bg-gradient-to-br from-purple-600 to-blue-500"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Culoare Card (clasa Tailwind)</label>
        <input
          type="text"
          name="cardBackground"
          value={themeData.cardBackground}
          onChange={handleThemeChange}
          className="w-full p-2 border rounded"
          placeholder="bg-white"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Culoare Text (clasa Tailwind)</label>
        <input
          type="text"
          name="textColor"
          value={themeData.textColor}
          onChange={handleThemeChange}
          className="w-full p-2 border rounded"
          placeholder="text-gray-800"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Culoare Hover Linkuri (clasa Tailwind)</label>
        <input
          type="text"
          name="linkHoverColor"
          value={themeData.linkHoverColor}
          onChange={handleThemeChange}
          className="w-full p-2 border rounded"
          placeholder="bg-gray-100"
        />
      </div>
    </div>
  );

  // Renderează tab-ul de linkuri
  const renderLinksTab = () => (
    <div>
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setShowAddLinkModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
        >
          <i className="fa-solid fa-plus mr-2"></i> Adaugă Link Nou
        </button>
        
        <button
          onClick={() => setShowAddFolderModal(true)}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 flex items-center"
        >
          <i className="fa-solid fa-folder-plus mr-2"></i> Adaugă Folder Nou
        </button>
      </div>

      <h3 className="text-xl font-medium mb-4">Organizează Link-uri și Foldere</h3>
      
      <Droppable droppableId="main-links" type="LINK">
        {(provided) => (
          <div
            className="space-y-4"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {links.map((link, index) => (
              <Draggable key={link.id.toString()} draggableId={link.id.toString()} index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className="bg-white rounded-lg p-4 shadow-md"
                    style={{
                      ...provided.draggableProps.style
                    }}
                  >
                    {link.type === 'folder' ? (
                      // Folder
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <span className="mr-2 text-gray-500"><i className="fas fa-grip-vertical"></i></span>
                            <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded mr-2">Folder</span>
                            <h4 className="font-medium">{link.title}</h4>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => setEditingFolderId(editingFolderId === link.id ? null : link.id)}
                              className="bg-yellow-100 text-yellow-700 p-1 rounded hover:bg-yellow-200"
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={() => deleteLink(link.id)}
                              className="bg-red-100 text-red-700 p-1 rounded hover:bg-red-200"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        {editingFolderId === link.id && (
                          <div className="mb-4 p-3 border rounded bg-gray-50">
                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">Platformă</label>
                              <select
                                name="platform"
                                value={link.platform}
                                onChange={(e) => handleLinkChange(e, link.id)}
                                className="w-full p-2 border rounded"
                              >
                                <option value="folder">Folder</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="globe">Resurse</option>
                              </select>
                            </div>
                            
                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">Titlu</label>
                              <input
                                type="text"
                                name="title"
                                value={link.title}
                                onChange={(e) => handleLinkChange(e, link.id)}
                                className="w-full p-2 border rounded"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">Icon</label>
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  name="icon"
                                  value={link.icon || ''}
                                  onChange={(e) => handleLinkChange(e, link.id)}
                                  className="flex-1 p-2 border rounded"
                                />
                                <button
                                  type="button"
                                  onClick={() => openIconSelector(`editLink-${link.id}`)}
                                  className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200"
                                >
                                  <i className="fa-solid fa-icons"></i> Alege
                                </button>
                              </div>
                              {link.icon && (
                                <div className="mt-2 p-2 border rounded flex items-center">
                                  <i className={`${link.icon} text-xl mr-2`}></i>
                                  <span className="text-sm text-gray-600">Preview iconiță</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="ml-6 mt-4">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium">Link-uri în Folder:</h5>
                            <button 
                              onClick={() => openAddChildLinkModal(link.id)}
                              className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm hover:bg-blue-200 flex items-center"
                            >
                              <i className="fa-solid fa-plus mr-1"></i> Adaugă Link
                            </button>
                          </div>
                          
                          <Droppable droppableId={`folder-${link.id}`} type="LINK">
                            {(provided) => (
                              <div
                                className="space-y-3"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {link.children && link.children.map((child, childIndex) => (
                                  <Draggable key={child.id.toString()} draggableId={child.id.toString()} index={childIndex}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="p-3 border rounded bg-gray-50"
                                        style={{
                                          ...provided.draggableProps.style
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center">
                                            <span className="mr-2 text-gray-500"><i className="fas fa-grip-vertical"></i></span>
                                            <h5 className="font-medium">{child.title}</h5>
                                          </div>
                                          
                                          <div className="flex space-x-2">
                                            <button 
                                              onClick={() => setEditingChildId(editingChildId === child.id ? null : child.id)}
                                              className="bg-yellow-100 text-yellow-700 p-1 rounded hover:bg-yellow-200"
                                            >
                                              ✏️
                                            </button>
                                            <button 
                                              onClick={() => deleteChildLink(link.id, child.id)}
                                              className="bg-red-100 text-red-700 p-1 rounded hover:bg-red-200"
                                            >
                                              🗑️
                                            </button>
                                          </div>
                                        </div>
                                        
                                        {editingChildId === child.id && (
                                          <div className="mt-3 p-3 border rounded bg-white">
                                            <div className="mb-3">
                                              <label className="block text-gray-700 mb-1">Platformă</label>
                                              <select
                                                name="platform"
                                                value={child.platform}
                                                onChange={(e) => handleChildLinkChange(e, link.id, child.id)}
                                                className="w-full p-2 border rounded"
                                              >
                                                <option value="globe">Website</option>
                                                <option value="facebook">Facebook</option>
                                                <option value="instagram">Instagram</option>
                                                <option value="tiktok">TikTok</option>
                                                <option value="youtube">YouTube</option>
                                                <option value="whatsapp">WhatsApp</option>
                                                <option value="telegram">Telegram</option>
                                                <option value="email">Email</option>
                                              </select>
                                            </div>
                                            
                                            <div className="mb-3">
                                              <label className="block text-gray-700 mb-1">Titlu</label>
                                              <input
                                                type="text"
                                                name="title"
                                                value={child.title}
                                                onChange={(e) => handleChildLinkChange(e, link.id, child.id)}
                                                className="w-full p-2 border rounded"
                                              />
                                            </div>
                                            
                                            <div className="mb-3">
                                              <label className="block text-gray-700 mb-1">URL</label>
                                              <input
                                                type="text"
                                                name="url"
                                                value={child.url}
                                                onChange={(e) => handleChildLinkChange(e, link.id, child.id)}
                                                className="w-full p-2 border rounded"
                                              />
                                            </div>

                                            <div className="mb-3">
                                              <label className="block text-gray-700 mb-1">Icon</label>
                                              <div className="flex space-x-2">
                                                <input
                                                  type="text"
                                                  name="icon"
                                                  value={child.icon || ''}
                                                  onChange={(e) => handleChildLinkChange(e, link.id, child.id)}
                                                  className="flex-1 p-2 border rounded"
                                                />
                                                <button
                                                  type="button"
                                                  onClick={() => openIconSelector(`editChildLink-${link.id}-${child.id}`)}
                                                  className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200"
                                                >
                                                  <i className="fa-solid fa-icons"></i> Alege
                                                </button>
                                              </div>
                                              {child.icon && (
                                                <div className="mt-2 p-2 border rounded flex items-center">
                                                  <i className={`${child.icon} text-xl mr-2`}></i>
                                                  <span className="text-sm text-gray-600">Preview iconiță</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      </div>
                    ) : (
                      // Link normal
                      <div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="mr-2 text-gray-500"><i className="fas fa-grip-vertical"></i></span>
                            <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2">Link</span>
                            <h4 className="font-medium">{link.title}</h4>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => setEditingLinkId(editingLinkId === link.id ? null : link.id)}
                              className="bg-yellow-100 text-yellow-700 p-1 rounded hover:bg-yellow-200"
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={() => deleteLink(link.id)}
                              className="bg-red-100 text-red-700 p-1 rounded hover:bg-red-200"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        {editingLinkId === link.id && (
                          <div className="mt-3 p-3 border rounded bg-gray-50">
                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">Platformă</label>
                              <select
                                name="platform"
                                value={link.platform}
                                onChange={(e) => handleLinkChange(e, link.id)}
                                className="w-full p-2 border rounded"
                              >
                                <option value="globe">Website</option>
                                <option value="facebook">Facebook</option>
                                <option value="instagram">Instagram</option>
                                <option value="tiktok">TikTok</option>
                                <option value="youtube">YouTube</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="telegram">Telegram</option>
                                <option value="email">Email</option>
                              </select>
                            </div>
                            
                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">Titlu</label>
                              <input
                                type="text"
                                name="title"
                                value={link.title}
                                onChange={(e) => handleLinkChange(e, link.id)}
                                className="w-full p-2 border rounded"
                              />
                            </div>
                            
                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">URL</label>
                              <input
                                type="text"
                                name="url"
                                value={link.url}
                                onChange={(e) => handleLinkChange(e, link.id)}
                                className="w-full p-2 border rounded"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="block text-gray-700 mb-1">Icon</label>
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  name="icon"
                                  value={link.icon || ''}
                                  onChange={(e) => handleLinkChange(e, link.id)}
                                  className="flex-1 p-2 border rounded"
                                />
                                <button
                                  type="button"
                                  onClick={() => openIconSelector(`editLink-${link.id}`)}
                                  className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200"
                                >
                                  <i className="fa-solid fa-icons"></i> Alege
                                </button>
                              </div>
                              {link.icon && (
                                <div className="mt-2 p-2 border rounded flex items-center">
                                  <i className={`${link.icon} text-xl mr-2`}></i>
                                  <span className="text-sm text-gray-600">Preview iconiță</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );

  // Renderează modalul de adăugare link
  const renderAddLinkModal = () => (
    <AddLinkModal
      newLink={newLink}
      setNewLink={setNewLink}
      onAddLink={addLink}
      onClose={() => setShowAddLinkModal(false)}
      openIconSelector={openIconSelector}
    />
  );

  // Renderează modalul de adăugare folder
  const renderAddFolderModal = () => (
    <AddFolderModal
      newFolder={newFolder}
      setNewFolder={setNewFolder}
      onAddFolder={addFolder}
      onClose={() => setShowAddFolderModal(false)}
      openIconSelector={openIconSelector}
    />
  );

  // Renderează modalul de adăugare link în folder
  const renderAddChildLinkModal = () => (
    <AddChildLinkModal
      currentFolder={links.find(link => link.id === currentFolderId)}
      newChildLink={newChildLink}
      setNewChildLink={setNewChildLink}
      onAddChildLink={() => addChildLink(currentFolderId)}
      onClose={() => setShowAddChildLinkModal(false)}
      openIconSelector={openIconSelector}
    />
  );

  // Funcție pentru a deschide modalul de adăugare link în folder
  const openAddChildLinkModal = (folderId) => {
    setCurrentFolderId(folderId);
    setShowAddChildLinkModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-medium ${
          notification.type === 'success' ? 'bg-green-500' : 
          notification.type === 'error' ? 'bg-red-500' : 
          'bg-yellow-500'
        }`}>
          {notification.message}
        </div>
      )}

      {showIconSelector && renderIconSelector()}
      {showAddLinkModal && renderAddLinkModal()}
      {showAddFolderModal && renderAddFolderModal()}
      {showAddChildLinkModal && renderAddChildLinkModal()}
      
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-2xl font-bold">🔧 Admin Panel - Linktree</h1>
          
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/')}
              className="bg-purple-500 text-white px-6 py-2 rounded hover:bg-purple-600 flex items-center transition-colors"
            >
              <i className="fa-solid fa-eye mr-2"></i> Preview Site
            </button>
            
            <button
              onClick={() => fileInputRef.current.click()}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center"
            >
              <i className="fa-solid fa-upload mr-2"></i> Încarcă JSON
            </button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
            
            <button
              onClick={saveJson}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 flex items-center"
            >
              <i className="fa-solid fa-save mr-2"></i> 
              {isElectron ? 'Salvează în /public/links.json' : 'Descarcă JSON'}
            </button>

            <button
              onClick={saveAndPreview}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
            >
              <i className="fa-solid fa-save mr-2"></i> 
              <i className="fa-solid fa-eye mr-2"></i> 
              Salvează & Preview
            </button>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="flex border-b">
            <button
              className={`px-4 py-2 ${activeTab === 'profile' ? 'bg-white border-t border-l border-r rounded-t text-blue-500' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setActiveTab('profile')}
            >
              Profil
            </button>
            <button
              className={`px-4 py-2 ${activeTab === 'theme' ? 'bg-white border-t border-l border-r rounded-t text-blue-500' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setActiveTab('theme')}
            >
              Temă
            </button>
            <button
              className={`px-4 py-2 ${activeTab === 'links' ? 'bg-white border-t border-l border-r rounded-t text-blue-500' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setActiveTab('links')}
            >
              Link-uri
            </button>
          </div>
        </div>
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <div>
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'theme' && renderThemeTab()}
            {activeTab === 'links' && renderLinksTab()}
          </div>
        </DragDropContext>
        
        <div className="mt-8 p-4 bg-gray-200 rounded">
          <h3 className="font-medium mb-2">Preview JSON:</h3>
          <pre className="bg-white p-4 rounded text-xs overflow-auto max-h-64">
            {generateJson()}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default JsonBuilder;